import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Steps } from "@freightsimple/ui-core/Display/Steps";
import { T } from "@freightsimple/ui-core/Typography/T";
import { useState } from "react";
import { Slider2 } from "@/Shared/Animations/CustomComponents/Slider";
import { SliderDirection } from "@/Shared/Animations/SliderDirection";
import useCreateAccount from "@/Shared/Authentication/Hooks/useAuthenticationCreateAccount";
import { AccountType } from "@/Shared/Authentication/Hooks/useAuthenticationCreateAccount";
import { AuthenticationCreateAccountPageAccountType } from "@/Shared/Authentication/CustomComponents/AuthenticationCreateAccountPageAccountType";
import { AuthenticationCreateAccountPageEmailAndPassword } from "@/Shared/Authentication/CustomComponents/AuthenticationCreateAccountPageEmailAndPassword";
import { AuthenticationCreateAccountPageNameAndPhoneNumber } from "@/Shared/Authentication/CustomComponents/AuthenticationCreateAccountPageNameAndPhoneNumber";
import { AuthenticationCreateAccountPageCompanyDetails } from "@/Shared/Authentication/CustomComponents/AuthenticationCreateAccountPageCompanyDetails";
import { AuthenticationCreateAccountStepButtons } from "@/Shared/Authentication/CustomComponents/AuthenticationCreateAccountStepButtons";

interface AuthenticationCreateAccountProps {
  onSignInInstead: () => void;
  onSuccess: () => void;
  prefilledEmail?: string | undefined;
  prefilledName?: string | undefined;
  prefilledCode?: string | undefined;
}

const Pages = {
  AccountType: 0,
  EmailAndPassword: 1,
  NameAndPhone: 2,
  CompanyDetails: 3,
} as const;
type Pages = (typeof Pages)[keyof typeof Pages];
const TOTAL_PAGES = 4;

// The transition takes ~500ms; lock input until it settles.
const TRANSITION_MS = 500;

export function AuthenticationCreateAccount(
  props: AuthenticationCreateAccountProps,
) {
  const { prefilledEmail, prefilledName, prefilledCode, onSignInInstead } =
    props;

  const [page, setPage] = useState<Pages>(Pages.AccountType);
  const [direction, setDirection] = useState<SliderDirection>(
    SliderDirection.Right,
  );
  const [forceValidation, setForceValidation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const {
    accountType,
    setAccountType,
    email,
    setEmail,
    password,
    setPassword,
    name,
    setName,
    phoneNumber,
    setPhoneNumber,
    phoneNumberExtension,
    setPhoneNumberExtension,
    companyName,
    setCompanyName,
    companyCurrency,
    setCompanyCurrency,
    shipmentFrequency,
    setShipmentFrequency,
    generalErrorMessage,
    createAccount,
    createAccountInProgress,
    isValid,
    errorMessages,
    isEmailUsed,
    checkEmailInProgress,
  } = useCreateAccount(
    prefilledEmail,
    prefilledName,
    prefilledCode,
    onSignInInstead,
  );

  async function onCreateAccount() {
    if (!isValid) {
      setForceValidation(true);
      return;
    }
    const success = await createAccount();
    if (success) {
      props.onSuccess();
    }
  }

  function isCurrentPageValid() {
    if (page === Pages.AccountType) {
      return errorMessages.accountType === undefined;
    }
    if (page === Pages.EmailAndPassword) {
      return (
        errorMessages.email === undefined &&
        errorMessages.password === undefined
      );
    }
    if (page === Pages.NameAndPhone) {
      return (
        errorMessages.name === undefined &&
        errorMessages.phoneNumber === undefined
      );
    }
    return (
      errorMessages.companyCurrency === undefined &&
      errorMessages.companyName === undefined &&
      errorMessages.shipmentFrequency === undefined
    );
  }

  function nextPage() {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setDirection(SliderDirection.Right);
    setPage((prev) => (prev + 1) as Pages);
    setTimeout(() => setIsTransitioning(false), TRANSITION_MS);
  }

  function previousPage() {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setDirection(SliderDirection.Left);
    setPage((prev) => (prev - 1) as Pages);
    setTimeout(() => setIsTransitioning(false), TRANSITION_MS);
  }

  async function checkEmailAndNextPage() {
    if (isTransitioning) return;
    setIsTransitioning(true);
    const isUsed = await isEmailUsed();
    if (!isUsed) {
      nextPage();
    } else {
      setIsTransitioning(false);
    }
  }

  async function handleNextClick() {
    if (isTransitioning) return;
    switch (page) {
      case Pages.AccountType:
        nextPage();
        break;
      case Pages.EmailAndPassword:
        await checkEmailAndNextPage();
        break;
      case Pages.NameAndPhone:
        nextPage();
        break;
      case Pages.CompanyDetails:
        setIsTransitioning(true);
        await onCreateAccount();
        setIsTransitioning(false);
        break;
    }
  }

  const isFirstPage = page === Pages.AccountType;
  const isLastPage = page === Pages.CompanyDetails;

  return (
    <ControlPresentationProvider presentation="underlined">
      <Stack gap={6}>
        <Logo />
        <Stack gap={1}>
          <Heading level={1} size="4xl" weight="semibold">
            Create Profile
          </Heading>
          <T size="sm" color="secondary">
            It takes under a minute to make your profile and gives you access to
            the best discounts, easy shipment management and billing.
          </T>
        </Stack>

        <Steps current={page} total={TOTAL_PAGES} />

        <Slider2 direction={direction} currentIndex={page} minHeight={250}>
          <AuthenticationCreateAccountPageAccountType
            accountType={accountType}
            setAccountType={setAccountType}
          />
          <AuthenticationCreateAccountPageEmailAndPassword
            accountType={accountType}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            forceValidation={forceValidation}
            errorMessages={errorMessages}
          />
          <AuthenticationCreateAccountPageNameAndPhoneNumber
            name={name}
            setName={setName}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            phoneNumberExtension={phoneNumberExtension}
            setPhoneNumberExtension={setPhoneNumberExtension}
            forceValidation={forceValidation}
            errorMessages={errorMessages}
          />
          <AuthenticationCreateAccountPageCompanyDetails
            accountType={accountType}
            isPersonalSelected={accountType === AccountType.Personal}
            companyName={companyName}
            setCompanyName={setCompanyName}
            companyCurrency={companyCurrency}
            setCompanyCurrency={setCompanyCurrency}
            shipmentFrequency={shipmentFrequency}
            setShipmentFrequency={setShipmentFrequency}
            forceValidation={forceValidation}
            errorMessages={errorMessages}
          />
        </Slider2>

        <Stack gap={4}>
          {generalErrorMessage && (
            <T color="red" align="center">
              {generalErrorMessage}
            </T>
          )}

          {isLastPage && (
            <Stack gap={1}>
              <T size="xs" color="muted">
                By creating an account, you agree to our{" "}
                <Link
                  href="https://www.freightsimple.com/terms-of-service"
                  external
                >
                  terms of service
                </Link>{" "}
                and{" "}
                <Link
                  href="https://www.freightsimple.com/privacy-policy"
                  external
                >
                  privacy policy
                </Link>
                . We&apos;ll occasionally send account-related emails.
              </T>
            </Stack>
          )}

          <AuthenticationCreateAccountStepButtons
            position={isFirstPage ? "first" : isLastPage ? "last" : "middle"}
            onNext={handleNextClick}
            onPrevious={previousPage}
            loading={
              createAccountInProgress || checkEmailInProgress || isTransitioning
            }
            disabled={!isCurrentPageValid() || isTransitioning}
          />

          <Stack align="center">
            <T color="secondary">
              Already have a profile?{" "}
              <Link onClick={onSignInInstead}>Sign in instead</Link>
            </T>
          </Stack>
        </Stack>
      </Stack>
    </ControlPresentationProvider>
  );
}
